import type React from 'react'
import { useState, useEffect } from 'react'
import { useSubDestMaster } from '../hooks/useSubDestMaster'
import { useDestMaster } from '../hooks/useDestMaster'
import { validateDestMaster, hasErrors } from '../utils/validation'
import type { SubDestMasterRequest, SubDestMasterResponse, FormErrors } from '../types/master'

interface SubDestMasterFormProps {
  subDestination?: SubDestMasterResponse | null
  onClose: () => void
  onSuccess: () => void
}

export default function SubDestMasterForm({
  subDestination,
  onClose,
  onSuccess,
}: SubDestMasterFormProps) {
  const { createSubDestination, updateSubDestination, loading } = useSubDestMaster()
  const { destinations, refetch: fetchDestinations } = useDestMaster()
  const [formData, setFormData] = useState<SubDestMasterRequest>({
    name: '',
    description: '',
    sort_num: null,
    dest_id: 0,
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  useEffect(() => {
    fetchDestinations()
  }, [])

  useEffect(() => {
    if (subDestination) {
      setFormData({
        name: subDestination.name,
        description: subDestination.description || '',
        sort_num: subDestination.sort_num,
        dest_id: subDestination.dest_id,
      })
    }
  }, [subDestination])

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target
    let processedValue: string | number | null = value
    if (name === 'sort_num') {
      processedValue = value === '' ? null : Number.parseInt(value, 10)
    } else if (name === 'dest_id') {
      processedValue = Number.parseInt(value, 10)
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue,
    }))

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }))
    }
  }

  const handleBlur = (field: string) => {
    setTouched(prev => ({
      ...prev,
      [field]: true,
    }))

    // Validate single field
    const fieldErrors = validateDestMaster(formData)
    setErrors(prev => ({
      ...prev,
      [field]: fieldErrors[field] || '',
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate all fields
    const validationErrors = validateDestMaster(formData)
    setErrors(validationErrors)

    if (hasErrors(validationErrors)) {
      return
    }

    try {
      if (subDestination) {
        await updateSubDestination(subDestination.id, formData)
      } else {
        await createSubDestination(formData)
      }
      onSuccess()
    } catch (error) {
      console.error('Failed to save sub destination:', error)
    }
  }

  const isEditing = !!subDestination

  return (
    <div
      className="modal show d-block"
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="fas fa-map-pin me-2" />
              {isEditing ? '利用先を編集' : '新規利用先を追加'}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} disabled={loading} />
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              <div className="mb-3">
                <label htmlFor="dest_id" className="form-label">
                  運用先 <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${touched.dest_id && errors.dest_id ? 'is-invalid' : ''}`}
                  id="dest_id"
                  name="dest_id"
                  value={formData.dest_id}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('dest_id')}
                  required
                >
                  {destinations.map(dest => (
                    <option key={dest.id} value={dest.id}>
                      {dest.name}
                    </option>
                  ))}
                </select>
                {touched.dest_id && errors.dest_id && (
                  <div className="invalid-feedback">{errors.dest_id}</div>
                )}
                <div className="form-text">この利用先が属する運用先を選択</div>
              </div>

              <div className="row">
                <div className="col-md-8">
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">
                      名前 <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${touched.name && errors.name ? 'is-invalid' : ''}`}
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('name')}
                      placeholder="利用先名を入力"
                      required
                    />
                    {touched.name && errors.name && (
                      <div className="invalid-feedback">{errors.name}</div>
                    )}
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="mb-3">
                    <label htmlFor="sort_num" className="form-label">
                      並び順
                    </label>
                    <input
                      type="number"
                      className={`form-control ${touched.sort_num && errors.sort_num ? 'is-invalid' : ''}`}
                      id="sort_num"
                      name="sort_num"
                      value={formData.sort_num || ''}
                      onChange={handleInputChange}
                      onBlur={() => handleBlur('sort_num')}
                      placeholder="任意"
                      min="0"
                      max="999999"
                    />
                    {touched.sort_num && errors.sort_num && (
                      <div className="invalid-feedback">{errors.sort_num}</div>
                    )}
                    <div className="form-text">表示順の制御に使用</div>
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="description" className="form-label">
                  説明
                </label>
                <textarea
                  className={`form-control ${touched.description && errors.description ? 'is-invalid' : ''}`}
                  id="description"
                  name="description"
                  value={formData.description || ''}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur('description')}
                  rows={3}
                  placeholder="説明を入力（任意）"
                />
                {touched.description && errors.description && (
                  <div className="invalid-feedback">{errors.description}</div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                キャンセル
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    {isEditing ? '更新中...' : '作成中...'}
                  </>
                ) : (
                  <>
                    <i className="fas fa-save me-2" />
                    {isEditing ? '更新' : '作成'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
